import React, { useEffect, useState } from "react";
import { Button, Divider, Modal, Select, Tag, message } from "antd";
import { LinearProgress } from "@mui/material";
import { Table as RadixTable } from "@radix-ui/themes";

import api from "../../../authentication/axiosInstance";
import styles from "./EquipmentAllocationTable.module.css";

const { Option } = Select;

export const formatPlowLabel = (plow) => {
  if (!plow) return "n/a";
  const plowModel = plow.plow_model || plow.plow_size || "Unknown model";
  const plowName = plow.plow_name || `Plow ${plow.plow_id}`;
  return `${plowModel} - ${plowName}`;
};

const VehicleDetailsModal = ({
  vehicle,
  onClose,
  availablePlows,
  availableDoubleDowns,
  loaderPath,
  fetchVehicles,
  fetchAvailablePlows,
  fetchAvailableDoubleDowns,
  fetchSites,
  pagination,
  onReplaceOpen,
  onDeallocateStaff,
  onSwapStaff,
  staffMutationBusy = false,
  deallocatingStaffId,
  refreshWarning,
  setupName,
  readOnly = false,
}) => {
  const [selectedPlowId, setSelectedPlowId] = useState(vehicle.assigned_plow?.plow_id || "n/a");
  const [selectedDoubleDown, setSelectedDoubleDown] = useState(vehicle.assigned_doubleDown_name || "n/a");

  useEffect(() => {
    setSelectedPlowId(vehicle.assigned_plow?.plow_id || "n/a");
  }, [vehicle.vehicle_id, vehicle.assigned_plow?.plow_id]);

  useEffect(() => {
    setSelectedDoubleDown(vehicle.assigned_doubleDown_name || "n/a");
  }, [vehicle.vehicle_id, vehicle.assigned_doubleDown_name]);

  const plowOptions = [...availablePlows];
  if (
    vehicle.assigned_plow
    && !plowOptions.some((plow) => plow.plow_id === vehicle.assigned_plow.plow_id)
  ) {
    plowOptions.unshift(vehicle.assigned_plow);
  }

  const maxCapacity = Number(vehicle.vehicle_capacity) || 0;
  const remainingCapacity = Number(vehicle.remaining_capacity) || 0;
  const usedCapacity = Math.max(maxCapacity - remainingCapacity, 0);
  const allocationPercentage = maxCapacity > 0
    ? Math.min(Math.max((usedCapacity / maxCapacity) * 100, 0), 100)
    : 0;
  const assignedStaff = Array.isArray(vehicle.assigned_staff) ? vehicle.assigned_staff : [];
  const allocatedSites = Array.isArray(vehicle.allocated_sites) ? vehicle.allocated_sites : [];
  const lockedSites = allocatedSites.filter(
    (site) => !site.archived && site.staff_allocation_finalized,
  );
  const staffActionsDisabled = lockedSites.length > 0 || deallocatingStaffId !== null || staffMutationBusy;
  const vehicleSubtitle = [
    vehicle.vehicle_type,
    vehicle.vehicle_subtype,
    vehicle.vehicle_model,
  ].filter(Boolean).join(" · ");
  const selectedPlow = selectedPlowId !== "n/a"
    ? plowOptions.find((plow) => String(plow.plow_id) === String(selectedPlowId))
      || vehicle.assigned_plow
    : null;
  const formatCapacity = (value) => `${(Number(value) || 0).toLocaleString()} sq. ft`;
  const lockExplanation = lockedSites.length === 1
    ? `Staff allocation is finalized for ${lockedSites[0].site_name || "this site"}. Unlock it from the site row to make changes.`
    : lockedSites.length > 1
      ? `Staff allocation is finalized for ${lockedSites[0].site_name || "one site"} and ${lockedSites.length - 1} additional ${lockedSites.length === 2 ? "site" : "sites"}. Unlock them from the site rows to make changes.`
      : null;

  const handlePlowChange = async (value) => {
    setSelectedPlowId(value);
    if (value === "n/a") {
      await handleDeallocatePlow();
      return;
    }

    try {
      await api.post(loaderPath(`vehicles/${vehicle.vehicle_id}/allocate-plow/`), {
        plow_id: value,
      });
    } catch (error) {
      console.error("Failed to assign plow:", error);
      message.error(error.response?.data?.error || "Failed to assign plow");
      setSelectedPlowId(vehicle.assigned_plow?.plow_id || "n/a");
      return;
    }
    fetchVehicles();
    fetchAvailablePlows();
    fetchSites(pagination.current, pagination.pageSize);
    message.success("Plow assigned successfully");
  };

  const handleDeallocatePlow = async () => {
    try {
      await api.post(loaderPath(`vehicles/${vehicle.vehicle_id}/deallocate-plow/`));
      fetchVehicles();
      fetchAvailablePlows();
      fetchSites(pagination.current, pagination.pageSize);
      setSelectedPlowId("n/a");
      message.success("Plow deallocated successfully");
    } catch (error) {
      console.error("Failed to deallocate plow:", error);
      message.error(error.response?.data?.error || "Failed to deallocate plow");
      setSelectedPlowId(vehicle.assigned_plow?.plow_id || "n/a");
    }
  };

  const handleDoubleDownChange = async (value) => {
    setSelectedDoubleDown(value);
    if (value === "n/a") {
      await handleDeallocateDoubleDown();
      return;
    }

    try {
      await api.post(loaderPath(`vehicles/${vehicle.vehicle_id}/allocate-doubledown/`), {
        doubleDown_name: value,
      });
      fetchVehicles();
      fetchAvailableDoubleDowns();
      message.success("Double Down assigned successfully");
    } catch (error) {
      console.error("Failed to assign Double Down:", error);
      message.error(error.response?.data?.error || "Failed to assign Double Down");
      setSelectedDoubleDown(vehicle.assigned_doubleDown_name || "n/a");
    }
  };

  const handleDeallocateDoubleDown = async () => {
    try {
      await api.post(loaderPath(`vehicles/${vehicle.vehicle_id}/deallocate-doubledown/`));
      fetchVehicles();
      fetchAvailableDoubleDowns();
      setSelectedDoubleDown("n/a");
      message.success("Double Down deallocated successfully");
    } catch (error) {
      console.error("Failed to deallocate Double Down:", error);
      message.error(error.response?.data?.error || "Failed to deallocate Double Down");
      setSelectedDoubleDown(vehicle.assigned_doubleDown_name || "n/a");
    }
  };

  return (
    <Modal
      title={(
        <div className={styles.vehicleModalHeader}>
          <span className={styles.vehicleModalEyebrow}>Vehicle Details</span>
          <div className={styles.vehicleModalTitleRow}>
            <div>
              <h2 className={styles.vehicleModalTitle}>{vehicle.vehicle_name || "Unnamed Vehicle"}</h2>
              {vehicleSubtitle && (
                <p className={styles.vehicleModalSubtitle}>{vehicleSubtitle}</p>
              )}
            </div>
            <div className={styles.vehicleModalTags}>
              {vehicle.archived && <Tag>Unavailable</Tag>}
              {vehicle.is_confirmed && <Tag color="green">Confirmed</Tag>}
              {vehicle.overallocated_to_site && <Tag color="orange">Overallocated</Tag>}
            </div>
          </div>
        </div>
      )}
      open
      onCancel={onClose}
      footer={null}
      centered
      width={760}
      destroyOnClose
    >
      <div className={styles.vehicleModalContent}>
        <section className={styles.vehicleModalSection}>
          <h3 className={styles.vehicleModalSectionTitle}>Capacity</h3>
          <div className={styles.vehicleCapacityProgress}>
            <LinearProgress
              variant="determinate"
              value={allocationPercentage}
              className={styles.vehicleCapacityProgressBar}
              sx={{
                "& .MuiLinearProgress-bar": {
                  backgroundColor: allocationPercentage >= 100 ? "#034832" : "#cdaf5e",
                },
              }}
            />
            <span>{allocationPercentage.toFixed(1)}% used</span>
          </div>
          <div className={styles.vehicleCapacityStats}>
            <div className={styles.vehicleCapacityStat}>
              <span>Used</span>
              <strong>{formatCapacity(usedCapacity)}</strong>
            </div>
            <div className={styles.vehicleCapacityStat}>
              <span>Remaining</span>
              <strong>{formatCapacity(remainingCapacity)}</strong>
            </div>
            <div className={styles.vehicleCapacityStat}>
              <span>Maximum</span>
              <strong>{formatCapacity(maxCapacity)}</strong>
            </div>
          </div>
        </section>

        <Divider />

        <section className={styles.vehicleModalSection}>
          <h3 className={styles.vehicleModalSectionTitle}>Assigned Staff</h3>
          {refreshWarning && (
            <p className={styles.vehicleModalWarning}>{refreshWarning}</p>
          )}
          {lockExplanation && (
            <p className={styles.vehicleModalWarning}>{lockExplanation}</p>
          )}
          {assignedStaff.length > 0 ? (
            <div className={styles.vehicleModalTableWrapper}>
              <RadixTable.Root size="1">
                <RadixTable.Header>
                  <RadixTable.Row>
                    <RadixTable.ColumnHeaderCell>Name</RadixTable.ColumnHeaderCell>
                    <RadixTable.ColumnHeaderCell>Position</RadixTable.ColumnHeaderCell>
                    <RadixTable.ColumnHeaderCell>Rating</RadixTable.ColumnHeaderCell>
                    <RadixTable.ColumnHeaderCell>Status</RadixTable.ColumnHeaderCell>
                    <RadixTable.ColumnHeaderCell>City</RadixTable.ColumnHeaderCell>
                    {!readOnly && <RadixTable.ColumnHeaderCell>Action</RadixTable.ColumnHeaderCell>}
                  </RadixTable.Row>
                </RadixTable.Header>
                <RadixTable.Body>
                  {assignedStaff.map((staff) => (
                    <RadixTable.Row key={staff.staff_id}>
                      <RadixTable.Cell><strong>{staff.name || "Unspecified"}</strong> {staff.archived && <Tag>Unavailable</Tag>}</RadixTable.Cell>
                      <RadixTable.Cell>{staff.position || "Unspecified"}</RadixTable.Cell>
                      <RadixTable.Cell>{staff.staff_rating ?? "Unspecified"}</RadixTable.Cell>
                      <RadixTable.Cell>{staff.status || "Unspecified"}</RadixTable.Cell>
                      <RadixTable.Cell>{staff.city || "Unspecified"}</RadixTable.Cell>
                      {!readOnly && <RadixTable.Cell>
                        <div className={styles.siteModalStaffActions}>
                          <Button
                            danger
                            size="small"
                            disabled={staffActionsDisabled}
                            loading={deallocatingStaffId === staff.staff_id}
                            onClick={() => {
                              Modal.confirm({
                                title: "Remove loader assignment?",
                                content: `Remove ${staff.name || "this staff member"} from ${vehicle.vehicle_name || "this loader"} in ${setupName || "the viewed setup"}? Loader, site, plow, and Double Down allocations will remain.`,
                                okText: "Remove assignment",
                                okButtonProps: { danger: true },
                                cancelText: "Cancel",
                                onOk: () => onDeallocateStaff(vehicle.vehicle_id, staff.staff_id),
                              });
                            }}
                          >
                            Deallocate Staff
                          </Button>
                          <Button
                            size="small"
                            className={styles.siteModalSwapButton}
                            aria-label={`Swap ${staff.name || "staff member"} on ${vehicle.vehicle_name || "this loader"}`}
                            aria-describedby={staff.archived ? `vehicle-staff-swap-unavailable-${staff.staff_id}` : undefined}
                            disabled={staffActionsDisabled || staff.archived || vehicle.archived || !onSwapStaff}
                            onClick={() => onSwapStaff(staff)}
                          >
                            Swap
                          </Button>
                          {staff.archived && (
                            <span
                              id={`vehicle-staff-swap-unavailable-${staff.staff_id}`}
                              className={styles.siteModalSwapDisabledReason}
                            >
                              Archived staff cannot be swapped.
                            </span>
                          )}
                        </div>
                      </RadixTable.Cell>}
                    </RadixTable.Row>
                  ))}
                </RadixTable.Body>
              </RadixTable.Root>
            </div>
          ) : (
            <p className={styles.vehicleModalEmptyState}>No staff assigned.</p>
          )}
        </section>

        <Divider />

        <section className={styles.vehicleModalSection}>
          <h3 className={styles.vehicleModalSectionTitle}>Equipment</h3>
          <div className={styles.vehicleEquipmentList}>
            <div className={styles.vehicleEquipmentRow}>
              <strong className={styles.vehicleEquipmentLabel}>Plow</strong>
              <div className={styles.vehicleEquipmentControl}>
                <Select
                  aria-label="Assigned plow"
                  placeholder="Select Plow"
                  onChange={handlePlowChange}
                  value={selectedPlowId}
                  disabled={readOnly}
                >
                  <Option value="n/a">None assigned</Option>
                  {plowOptions.map((plow) => (
                    <Option key={plow.plow_id} value={plow.plow_id}>
                      {formatPlowLabel(plow)}
                    </Option>
                  ))}
                </Select>
                {selectedPlow ? (
                  <span className={styles.vehicleEquipmentMeta}>
                    {selectedPlow.archived ? "Unavailable" : (selectedPlow.is_confirmed ? "Confirmed" : "Unconfirmed")}
                  </span>
                ) : (
                  <span className={styles.vehicleEquipmentMeta}>No plow assigned</span>
                )}
              </div>
              {!readOnly && selectedPlowId !== "n/a" && (
                <Button onClick={handleDeallocatePlow}>Deallocate Plow</Button>
              )}
            </div>

            <div className={styles.vehicleEquipmentRow}>
              <strong className={styles.vehicleEquipmentLabel}>Double Down</strong>
              <div className={styles.vehicleEquipmentControl}>
                <Select
                  aria-label="Assigned Double Down"
                  placeholder="Select Double Down"
                  onChange={handleDoubleDownChange}
                  value={selectedDoubleDown}
                  disabled={readOnly}
                >
                  <Option value="n/a">None assigned</Option>
                  {availableDoubleDowns.map((doubleDown) => (
                    <Option key={doubleDown.doubleDown_id} value={doubleDown.doubleDown_name}>
                      {doubleDown.doubleDown_name}
                    </Option>
                  ))}
                </Select>
                {selectedDoubleDown !== "n/a" && vehicle.assigned_doubleDown ? (
                  <span className={styles.vehicleEquipmentMeta}>
                    {[vehicle.assigned_doubleDown.doubleDown_model, vehicle.assigned_doubleDown.doubleDown_type_name]
                      .filter(Boolean)
                      .join(" · ") || "Details unavailable"}
                    {vehicle.assigned_doubleDown.archived ? " · Unavailable" : ""}
                  </span>
                ) : (
                  <span className={styles.vehicleEquipmentMeta}>No Double Down assigned</span>
                )}
              </div>
              {!readOnly && selectedDoubleDown !== "n/a" && (
                <Button onClick={handleDeallocateDoubleDown}>Deallocate Double Down</Button>
              )}
            </div>
          </div>
        </section>

        <Divider />

        <section className={styles.vehicleModalSection}>
          <div className={styles.vehicleModalSectionHeading}>
            <h3 className={styles.vehicleModalSectionTitle}>Assigned Sites</h3>
            <span>{allocatedSites.length} {allocatedSites.length === 1 ? "site" : "sites"}</span>
          </div>
          {vehicle.overallocated_to_site && (
            <p className={styles.vehicleModalWarning}>
              This vehicle has an overallocated site assignment.
            </p>
          )}
          {allocatedSites.length > 0 ? (
            <div className={styles.vehicleModalTableWrapper}>
              <RadixTable.Root size="1">
                <RadixTable.Header>
                  <RadixTable.Row>
                    <RadixTable.ColumnHeaderCell>Client</RadixTable.ColumnHeaderCell>
                    <RadixTable.ColumnHeaderCell>Site</RadixTable.ColumnHeaderCell>
                    <RadixTable.ColumnHeaderCell>Allocated Capacity</RadixTable.ColumnHeaderCell>
                  </RadixTable.Row>
                </RadixTable.Header>
                <RadixTable.Body>
                  {allocatedSites.map((siteAllocation) => (
                    <RadixTable.Row key={siteAllocation.site_id}>
                      <RadixTable.Cell>{siteAllocation.client_name || "Unspecified"}</RadixTable.Cell>
                      <RadixTable.Cell>
                        <strong>{siteAllocation.site_name || `Site ${siteAllocation.site_id}`}</strong> {siteAllocation.archived && <Tag>Unavailable</Tag>}
                      </RadixTable.Cell>
                      <RadixTable.Cell>{formatCapacity(siteAllocation.allocated_capacity)}</RadixTable.Cell>
                    </RadixTable.Row>
                  ))}
                </RadixTable.Body>
              </RadixTable.Root>
            </div>
          ) : (
            <p className={styles.vehicleModalEmptyState}>No sites assigned to this vehicle.</p>
          )}
        </section>

        {!readOnly && <div className={styles.vehicleModalActions}>
          <Button
            type="primary"
            className={styles.vehicleReplaceButton}
            onClick={() => onReplaceOpen(vehicle.vehicle_id)}
          >
            Replace Vehicle
          </Button>
        </div>}
      </div>
    </Modal>
  );
};

export default VehicleDetailsModal;
